import { isAxiosError } from "axios";
import { motion } from "framer-motion";
import { debounce } from "es-toolkit/compat";
import { useCallback, useEffect, useRef, useState } from "react";
import { LoaderFunction, useLoaderData, useNavigate } from "react-router-dom";

import { ANIMATION_DURATION } from "@/constants/animations.constants";
import { useTranslation } from "@/core/i18n";
import { AccountSetupLoading } from "@/modules/onboarding/components/AccountSetupLoading/AccountSetupLoading";
import { MobileViewportContainer } from "@/modules/onboarding/components/MobileViewportContainer/MobileViewportContainer";
import { OnboardingBackButton } from "@/modules/onboarding/components/OnboardingBackButton/OnboardingBackButton";
import OnboardingHeaderV3 from "@/modules/onboarding/components/OnboardingScreenHeader/OnboardingHeaderV3";
import { PlatformSearchModal } from "@/modules/onboarding/components/PlatformSearchModal/PlatformSearchModal";
import { hasCompleteProfile, useOnboardingContext } from "@/modules/onboarding/context";
import { QRScanner } from "@/organisms/qr-scanner/QRScanner";
import { getMultiPlatformList, getMultiPlatformPlatformQrcode } from "api/methods";
import { Models } from "api/types";
import { Button } from "atoms/button/Button";
import { queryClient } from "pages/Root";
import { waitObj } from "toolbox/Promise";
import { isNativeMobileApp } from "utils/platform.helpers";

const load = async () => {
  // We still load the platform tokens to maintain session state if needed
  let platformDomainTokens: Models<"GetMultiPlatformUserPlatformResult">[] = [];
  try {
    platformDomainTokens = await queryClient
      .fetchQuery({ ...getMultiPlatformList.keyed() })
      .then((res) => res.platformDomainTokens);
  } catch {
    // If the list fails to load, leave it empty
  }

  return waitObj({ platformDomainTokens });
};

type LoaderData = Awaited<ReturnType<typeof load>>;

export const v3PlatformPickerLoader: LoaderFunction = () => {
  return load();
};

const getPlatformIdFromScannedCodeDebounced = debounce(
  (scannedCode: string) => getMultiPlatformPlatformQrcode({ scannedCode }),
  500,
  { leading: true, trailing: false },
);

const V3PlatformPicker = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { platformDomainTokens } = useLoaderData() as LoaderData;
  const { setPlatformId, updateFeatures, updatePlatformSelection, profile, loadExistingUserData } =
    useOnboardingContext();
  const profileAlreadyComplete = hasCompleteProfile(profile);

  // When an existing user adds a location, hide practices they already belong to
  // so selecting one cannot route them back through new-user onboarding.
  const existingPlatformIds = platformDomainTokens
    .map((platform) => platform.platformId)
    .filter((platformId): platformId is string => !!platformId);

  // Authenticated users can reach this screen via "Add a new location" without
  // going through verification, so the onboarding context profile is empty.
  // Hydrate it from /user once on mount so the routing decision (and downstream
  // profile update) uses their real profile instead of forcing onboarding.
  const hydrationRef = useRef<ReturnType<typeof loadExistingUserData> | null>(null);
  useEffect(() => {
    hydrationRef.current = loadExistingUserData();
  }, [loadExistingUserData]);

  // Resolve profile completeness and return the destination route. Always await
  // the in-flight hydration first: loadExistingUserData calls setPlatformId as a
  // side effect, so it must settle before the caller sets the selected platform,
  // otherwise a late hydration would overwrite the user's chosen practice.
  const resolveNextRoute = useCallback(async (): Promise<string> => {
    const user = await (hydrationRef.current ?? loadExistingUserData());
    const complete = !!(user?.firstName && user?.lastName && user?.email && user?.gender);
    return profileAlreadyComplete || complete ? "/signin/location" : "/signin/name";
  }, [profileAlreadyComplete, loadExistingUserData]);

  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showErrorMessage, setShowErrorMessage] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isQrScanInProgress, setIsQrScanInProgress] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);

  const handleScanError = (error: Error) => {
    const errorStatus = isAxiosError(error) ? error.response?.status : null;
    if (errorStatus === 400 || errorStatus === 401) {
      setErrorMessage(t("v3PlatformPicker.qrCodeUnrecognized"));
    } else {
      setErrorMessage(t("v3PlatformPicker.qrCodeGenericError"));
    }
    setShowErrorMessage(true);
  };

  // QR Scanner success handling
  const handleScannedCode = useCallback(
    async (scannedCode: string) => {
      try {
        const { platformId, platformLocationId } =
          await getPlatformIdFromScannedCodeDebounced(scannedCode);
        if (!platformId || !platformLocationId) {
          return;
        }

        setShowQRScanner(false);
        updateFeatures({ hasScannedQRCode: true });

        // Resolve the route (awaiting hydration) before selecting the platform so
        // the newly selected platform is not overwritten by hydration side effects.
        const nextRoute = await resolveNextRoute();
        setPlatformId(platformId);

        navigate(nextRoute);
      } catch (error) {
        handleScanError(error as Error);
        setIsQrScanInProgress(false);
      }
    },
    [navigate, setPlatformId, updateFeatures, resolveNextRoute],
  );

  const handleSearchForPlatform = () => {
    setShowSearchModal(true);
  };

  const handlePlatformSelect = async (platformId: string) => {
    setShowSearchModal(false);
    updatePlatformSelection({ canReselectPlatform: true });

    // Resolve the route (awaiting hydration) before selecting the platform so
    // the newly selected platform is not overwritten by hydration side effects.
    const nextRoute = await resolveNextRoute();
    setPlatformId(platformId);

    navigate(nextRoute);
  };

  useEffect(() => {
    if (showErrorMessage) {
      const timeout = setTimeout(() => {
        setShowErrorMessage(false);
        setErrorMessage("");
      }, 2000);

      return () => clearTimeout(timeout);
    }
  }, [showErrorMessage]);

  return (
    <>
      {showQRScanner && (
        <QRScanner
          errorMessage={showErrorMessage ? errorMessage : undefined}
          onClose={() => {
            setShowQRScanner(false);
            setShowErrorMessage(false);
            setErrorMessage("");
          }}
          onResult={handleScannedCode}
        />
      )}

      <PlatformSearchModal
        open={showSearchModal}
        onClose={() => setShowSearchModal(false)}
        onPlatformSelect={handlePlatformSelect}
        excludedPlatformIds={existingPlatformIds}
      />

      <MobileViewportContainer header={<OnboardingBackButton />}>
        {isQrScanInProgress ? (
          <AccountSetupLoading />
        ) : (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: ANIMATION_DURATION, delay: 0.1 }}
            className="flex w-full max-w-sm flex-col items-center gap-9"
          >
            {/* Header Section */}
            <OnboardingHeaderV3
              title={t("v3PlatformPicker.title")}
              subtitle={
                <>
                  <p className="mb-1.5">{t("v3PlatformPicker.subtitleSearch")}</p>
                  <p>
                    {t("v3PlatformPicker.subtitleAlreadyMemberLine1")}
                    <br />
                    {t("v3PlatformPicker.subtitleAlreadyMemberLine2")}
                  </p>
                </>
              }
              align="left"
            />

            {/* Buttons Section */}
            <div className="flex w-full flex-col gap-4">
              <Button size="large" style="primary" fullWidth onClick={handleSearchForPlatform}>
                {t("v3PlatformPicker.searchButton")}
              </Button>

              {isNativeMobileApp() && (
                <Button
                  size="large"
                  style="secondary"
                  fullWidth
                  onClick={() => setShowQRScanner(true)}
                >
                  {t("v3PlatformPicker.scanQrButton")}
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </MobileViewportContainer>
    </>
  );
};

export default V3PlatformPicker;
